<template>
  <Transition
    enter-active-class="tw:transition-opacity tw:duration-300 tw:ease-in-out tw:delay-300"
    enter-from-class="tw:opacity-0"
    leave-active-class="tw:transition-opacity tw:duration-[0ms]"
    leave-to-class="tw:opacity-0"
    mode="out-in"
  >
    <EdroneTooltip
      v-if="
        actor &&
        props.show &&
        !actor.snapshot.value.hasTag('fetchingData') &&
        !actor.snapshot.value.hasTag('error') &&
        !paying
      "
      type="custom"
      position="right"
      class="tw:!w-full"
      trigger-event="mouseenter click"
    >
      <div
        class="tw:p-3 tw:border tw:border-branding-600 tw:rounded-sm tw:bg-[linear-gradient(160deg,_rgba(255,255,140,0.5)_4.95%,_#FFFFE0_47.89%)] tw:flex tw:flex-col tw:gap-3"
      >
        <div class="tw:flex tw:flex-col tw:gap-1">
          <div class="tw:flex tw:gap-1 tw:justify-between tw:items-center">
            <p class="tw:caption tw:text-default-700">{{ trans._freemium.banner.title }}</p>
          </div>
          <div class="tw:flex tw:gap-1 tw:flex-col">
            <div class="tw:flex tw:justify-between tw:items-center">
              <p class="tw:body-2--bold tw:text-black">
                {{ trans._freemium.banner.messages.label }}
              </p>
              <EdroneBadge
                :label="`${usedMessageCredits}`"
                :type="usedMessageCredits ? 'info' : 'error'"
                size="small"
              />
            </div>
            <EdroneProgressBar :badge="false" :value="messagesPercent" :type="usedMessageCredits ? 'info' : 'error'" />
            <p v-if="usedMessageCredits === 0" class="tw:caption tw:text-default-700">
              {{ trans._freemium.banner.outOfMessages }}
            </p>
          </div>
        </div>
        <EdroneButton
          :disabled="!actor.snapshot.value.can({ type: 'UPGRADE_PLAN' })"
          size="medium"
          :type="usedMessageCredits ? 'outline' : 'solid'"
          @click-self="handleUpgradeClick"
        >
          {{ trans._freemium.banner.buttons.upgrade }}
          <template #right-icon>🚀</template>
        </EdroneButton>
      </div>
      <template #custom>
        <div class="tw:flex tw:flex-col tw:gap-4">
          <div>
            <p class="tw:caption tw:font-medium tw:text-default-700">{{ trans._freemium.tooltip.title }}</p>
            <ul>
              <li class="tw:caption tw:text-default-700">
                <span>
                  {{ trans._freemium.tooltip.list.messages.split("{messages}")[0] }}
                  <b>{{ usedMessageCredits }}</b>
                  {{ trans._freemium.tooltip.list.messages.split("{messages}")[1].split("{limit}")[0] }}
                  <b>{{ limitMessageCredits }}</b>
                  {{ trans._freemium.tooltip.list.messages.split("{limit}")[1] }}
                </span>
              </li>
              <li class="tw:caption tw:text-default-700">
                <span>
                  {{ trans._freemium.tooltip.list.renews.split("{days}")[0] }}
                  <b>{{ daysToReset }}</b>
                  {{ trans._freemium.tooltip.list.renews.split("{days}")[1] }}
                </span>
              </li>
            </ul>
          </div>
          <div>
            <div class="tw:flex tw:gap-1">
              <p class="tw:caption tw:text-default-700">{{ trans._freemium.tooltip.more.title }}</p>
              <p class="tw:caption tw:text-default-700 tw:font-medium">{{ trans._freemium.tooltip.more.title2 }}</p>
            </div>
            <ul class="tw:list-disc tw:pl-4">
              <li class="tw:list-disc tw:list-item tw:caption tw:text-default-700">
                {{ trans._freemium.tooltip.more.list.unlimitedMessages }}.
              </li>
              <li class="tw:list-disc tw:list-item tw:caption tw:text-default-700">
                {{ trans._freemium.tooltip.more.list.unlimitedSms }}.
              </li>
            </ul>
          </div>
        </div>
      </template>
    </EdroneTooltip>
  </Transition>
</template>

<script lang="ts" setup>
import { computed, inject, onUnmounted } from "vue";
import { storeToRefs } from "pinia";
import { amplitudeSendEvent } from "vue-app/utils/amplitude.ts";
import { useFreemiumStore } from "vue-app/components/Freemium/Freemium.store.ts";
import { symbolMachineFreemium } from "vue-app/components/Freemium/Freemium.machine.ts";
import EdroneTooltip from "edrone-components/EdroneTooltip.vue";
import EdroneProgressBar from "edrone-components/EdroneProgressBar.vue";
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneBadge from "edrone-components/EdroneBadge/EdroneBadge.vue";

interface Props {
  show?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
});

const store = useFreemiumStore();
const { daysToReset, usedMessageCredits, paying, limitMessageCredits } = storeToRefs(store);
const actor = inject(symbolMachineFreemium);

const messagesPercent = computed(() => (usedMessageCredits.value / limitMessageCredits.value) * 100);

const handleUpgradeClick = (event: MouseEvent) => {
  event.stopPropagation();
  amplitudeSendEvent("Freemium_Banner_Upgrade_Clicked", {
    button_type: usedMessageCredits.value ? "outline" : "solid",
    out_of_messages: usedMessageCredits.value >= limitMessageCredits.value,
  });
  actor?.send({ type: "UPGRADE_PLAN" });
};

const pubsubToken = window.pubSub.subscribe("freemium.upgrade", () => {
  amplitudeSendEvent("Freemium_Toast_Upgrade_Clicked", {
    button_type: "solid",
    out_of_messages: true,
  });
  actor?.send({ type: "UPGRADE_PLAN" });
});

onUnmounted(() => {
  window.pubSub.unsubscribe(pubsubToken);
});
</script>
